import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { initiateEasebuzzPayment } from "../../services/paymentService";

// ── Thunks ───────────────────────────────────────────────────────────────────

export const initiateEasebuzzPaymentThunk = createAsyncThunk(
    "payment/initiateEasebuzz",
    async (paymentData, { rejectWithValue }) => {
        try {
            return await initiateEasebuzzPayment(paymentData);
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Payment initiation failed"
            );
        }
    }
);

export const initiateEasebuzzPaymentThunkRecharge = createAsyncThunk(
    "payment/initiateEasebuzzRecharge",
    async (paymentData, { rejectWithValue }) => {
        try {
            return await initiateEasebuzzPayment(paymentData);
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Payment initiation failed"
            );
        }
    }
);

export const verifyEasebuzzPayment = createAsyncThunk(
    "payment/verifyEasebuzz",
    async (verificationData, { rejectWithValue }) => {
        try {
            return {
                status: verificationData.status || "success",
                transaction_id: verificationData.txn_id,
                amount: verificationData.amount,
                email: verificationData.email,
            };
        } catch (error) {
            return rejectWithValue(
                error.message || "Payment verification failed"
            );
        }
    }
);

// ── Initial State ────────────────────────────────────────────────────────────

const initialState = {
    loading: false,
    error: null,

    paymentInitiated: false,
    paymentStatus: "idle",
    transactionId: null,
    paymentData: null,

    loadingRecharge: false,
    errorRecharge: null,

    paymentInitiatedRecharge: false,
    paymentStatusRecharge: "idle",
    transactionIdRecharge: null,
    paymentDataRecharge: null,
};

// ── Slice ────────────────────────────────────────────────────────────────────

const paymentSlice = createSlice({
    name: "payment",
    initialState,

    reducers: {
        clearError: (state) => {
            state.error = null;
            state.errorRecharge = null;
        },

        resetPayment: () => initialState,

        setPaymentStatus: (state, action) => {
            state.paymentStatus = action.payload.status;
            state.transactionId = action.payload.transaction_id;
        },

        handleEasebuzzResponse: (state, action) => {
            const response = action.payload;

            if (response.txnid) {
                state.transactionId = response.txnid;
                state.paymentStatus =
                    response.status === "success"
                        ? "success"
                        : "failed";
            }
        },
    },

    extraReducers: (builder) => {
        builder

            // ── Primary Payment ──────────────────────────────────────────────

            .addCase(initiateEasebuzzPaymentThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.paymentInitiated = false;
                state.paymentStatus = "idle";
            })

            .addCase(
                initiateEasebuzzPaymentThunk.fulfilled,
                (state, action) => {
                    state.loading = false;
                    state.paymentInitiated = true;
                    state.paymentStatus = "initiated";
                    state.paymentData = action.payload;
                }
            )

            .addCase(
                initiateEasebuzzPaymentThunk.rejected,
                (state, action) => {
                    state.loading = false;
                    state.error = action.payload;
                    state.paymentInitiated = false;
                    state.paymentStatus = "failed";
                }
            )

            // ── Recharge / Top-up Payment ────────────────────────────────────

            .addCase(
                initiateEasebuzzPaymentThunkRecharge.pending,
                (state) => {
                    state.loadingRecharge = true;
                    state.errorRecharge = null;
                    state.paymentInitiatedRecharge = false;
                    state.paymentStatusRecharge = "idle";
                }
            )

            .addCase(
                initiateEasebuzzPaymentThunkRecharge.fulfilled,
                (state, action) => {
                    state.loadingRecharge = false;
                    state.paymentInitiatedRecharge = true;
                    state.paymentStatusRecharge = "initiated";
                    state.paymentDataRecharge = action.payload;
                }
            )

            .addCase(
                initiateEasebuzzPaymentThunkRecharge.rejected,
                (state, action) => {
                    state.loadingRecharge = false;
                    state.errorRecharge = action.payload;
                    state.paymentInitiatedRecharge = false;
                    state.paymentStatusRecharge = "failed";
                }
            )

            // ── Verification ─────────────────────────────────────────────────

            .addCase(verifyEasebuzzPayment.pending, (state) => {
                state.loading = true;
                state.error = null;
            })

            .addCase(verifyEasebuzzPayment.fulfilled, (state, action) => {
                state.loading = false;
                state.paymentStatus = action.payload.status;
                state.transactionId = action.payload.transaction_id;
            })

            .addCase(
                verifyEasebuzzPayment.rejected,
                (state, action) => {
                    state.loading = false;
                    state.error = action.payload;
                    state.paymentStatus = "failed";
                }
            );
    },
});

export const {
    clearError,
    resetPayment,
    setPaymentStatus,
    handleEasebuzzResponse,
} = paymentSlice.actions;

export default paymentSlice.reducer;

// ── Selectors ────────────────────────────────────────────────────────────────

export const selectPaymentState = (state) => state.payment;